import {DeploymentAccount} from '../types';
import {DeploymentAccountGroupAccount} from '../../deployment-account-groups/types';

// FNP-450 / FNP-1017: canonical platform order for deployment accounts —
// DEV before TEST before PROD. Mirror of the backend sort in
// archetype-defaults.ts:sortDeploymentAccountsByEnvironment. Sorting here as
// defense in depth so the displayed list matches the pipeline that gets
// provisioned even if the backend ever loses the sort.
//
// FNP-1017: dev previously fell into the catch-all bucket and therefore ranked
// AFTER prod. The backend hit the identical bug when its DEV filter came off
// (0aaa8b6) — with dev ranked last, the CFT builder's POSITIONAL fallback pairs
// the prod stage with the dev account. Ranking dev(0)/test(1)/prod(2) keeps the
// client's displayed order identical to the array the backend pairs against.
export const sortByEnvironment = (accounts: DeploymentAccount[]): DeploymentAccount[] => {
    const rank = (acct: DeploymentAccount): number => {
        const name = (acct.accountName || '').toLowerCase();
        if (name.endsWith('-dev') || name.includes('dev')) return 0;
        if (name.endsWith('-test') || name.includes('test')) return 1;
        if (name.endsWith('-prod') || name.includes('prod')) return 2;
        return 3;
    };
    return [...accounts].sort((a, b) => rank(a) - rank(b));
};

// FNP-1017: derive the form's `deploymentAccounts` from a selected account group.
// Exported so the DEV-account regression is asserted against the REAL
// transformation rather than a copy of it in a test.
//
// ⚠️ NO environment filter. This previously kept only TEST+PROD, which silently
// discarded every DEV account the moment the admin picked an account group — so
// the DeployDev ladder rung rendered permanently disabled ("no dev account")
// even though pipelines-api 657e86c removed the same filter server-side and DEV
// accounts really are returned. Do NOT reintroduce a filter here: a rung with no
// matching account is meant to be disabled because the OU genuinely lacks one,
// never because the client threw the account away.
export const deriveDeploymentAccounts = (
    groupAccounts: DeploymentAccountGroupAccount[],
    previousBucketByAccountId: Record<string, string | undefined>,
    repositoryName?: string,
): DeploymentAccount[] =>
    sortByEnvironment(
        groupAccounts.map((a) => ({
            accountId: a.accountId,
            accountName: a.accountName,
            region: a.region,
            // FNP-694: preserve a user-edited bucket for the same account if one
            // exists; otherwise DERIVE it from the same convention the backend
            // analyzer uses (${repositoryName}-infra-${accountId}-ui). Without this,
            // switching to a different OU (whose accounts have new accountIds) left
            // every bucket field empty because previousBucketByAccountId never matched.
            deploymentBucketName: previousBucketByAccountId[a.accountId]
                ?? (repositoryName
                    ? `${repositoryName}-infra-${a.accountId}-ui`
                    : undefined),
        })),
    );
